import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { DialogModal, Dropdown, Modal } from '@/components';
import {
	CREATE_EVENT_SUBMIT_LABEL,
	CREATE_EVENT_TITLE,
	EDIT_EVENT_SUBMIT_LABEL,
	EDIT_EVENT_TITLE,
	EVENT_LINK_PLACEHOLDER,
	EVENT_NAME_MAX_LENGTH,
	EVENT_NAME_PLACEHOLDER,
	EVENT_PLATFORM_OPTIONS,
	EVENT_TYPE_CODE,
	EVENT_SHORT_DESCRIPTION_MAX_LENGTH,
	EVENT_SHORT_DESCRIPTION_PLACEHOLDER,
	EVENT_ERROR_MESSAGE,
	EVENT_GROUPS_EDIT_LOCKED_TEXT,
	EVENT_GROUPS_EMPTY_TEXT,
	MIXED_GROUPS_DIALOG,
	ParticipantMode,
	ParticipantModeLabel,
	SELECT_PLACEHOLDER,
	STUDENT_PICKER_SUBMIT_LABEL
} from '../../boutique-classroom.constants';
import {
	AcademicBoardBranch,
	AcademicBoardExpert,
	CreateEventFormErrors,
	CreateEventFormField,
	CreateEventFormValues,
	EventPlatformOption,
	ExpertEventType,
	ParticipantModeValue,
	SelectItem,
	StudentPickerItem
} from '../../boutique-classroom.types';
import StepHeading from './StepHeading';
import EventTypeCard from './EventTypeCard';
import FormField from './FormField';
import DateTimeInput from './DateTimeInput';
import StudentPickerList from './StudentPickerList';
import GroupMultiSelect from './GroupMultiSelect';
import { formatDuration, getEarliestStartTimeForToday, hasStudentsFromMultipleGroups, isSameDayAsToday, toEventFormValues, validateCreateEventValues } from './create-event-modal.utils';
import { BoutiqueCalendarLayoutEvent } from '../boutique-calendar/boutique-calendar.types';
import NotificationManager from '@/managers/notification-manager.tsx';
import { NOTIFICATION_TYPES } from '@/constants/notifications';
import '@/assets/styles/views/boutique-classroom/create-event-modal.styles.scss';

interface CreateEventModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (values: CreateEventFormValues) => void;
	onCreateGroup?: (selectedStudents: StudentPickerItem[]) => void;
	eventTypes?: ExpertEventType[];
	branches?: AcademicBoardBranch[];
	experts?: AcademicBoardExpert[];
	groups?: SelectItem[];
	students?: StudentPickerItem[];
	isSubmitting?: boolean;
	isLoadingOptions?: boolean;
	editingEvent?: BoutiqueCalendarLayoutEvent | null;
}

const PARTICIPANT_MODES = Object.values(ParticipantMode);
const EMPTY_LIST: SelectItem[] = [];
const EMPTY_STUDENTS: StudentPickerItem[] = [];
const EMPTY_TYPES: ExpertEventType[] = [];
const EMPTY_BRANCHES: AcademicBoardBranch[] = [];
const EMPTY_EXPERTS: AcademicBoardExpert[] = [];
const PLATFORM_OPTIONS: EventPlatformOption[] = EVENT_PLATFORM_OPTIONS.map(option => ({ ...option }));

const INITIAL_VALUES: CreateEventFormValues = {
	eventType: null,
	name: '',
	shortDescription: '',
	platform: null,
	participationLink: '',
	branch: null,
	expert: null,
	date: null,
	startTime: null,
	endTime: null,
	participantMode: ParticipantMode.GROUP,
	groups: [],
	studentIds: []
};

export const CreateEventModal: React.FC<CreateEventModalProps> = ({
	isOpen,
	onClose,
	onSubmit,
	onCreateGroup,
	eventTypes = EMPTY_TYPES,
	branches = EMPTY_BRANCHES,
	experts = EMPTY_EXPERTS,
	groups = EMPTY_LIST,
	students = EMPTY_STUDENTS,
	isSubmitting = false,
	isLoadingOptions = false,
	editingEvent = null
}) => {
	const [values, setValues] = useState<CreateEventFormValues>(INITIAL_VALUES);
	const [touchedFields, setTouchedFields] = useState<Set<CreateEventFormField>>(() => new Set());
	const [isSubmitAttempted, setIsSubmitAttempted] = useState(false);

	const patch = useCallback(<K extends CreateEventFormField>(key: K, value: CreateEventFormValues[K]) => {
		setValues(prev => ({ ...prev, [key]: value }));
	}, []);

	const markTouched = useCallback((field: CreateEventFormField) => {
		setTouchedFields(prev => {
			if (prev.has(field)) return prev;
			const next = new Set(prev);
			next.add(field);
			return next;
		});
	}, []);

	const patchAndTouch = useCallback(
		<K extends CreateEventFormField>(key: K, value: CreateEventFormValues[K]) => {
			patch(key, value);
			markTouched(key);
		},
		[markTouched, patch]
	);

	const isEditMode = editingEvent !== null;

	useEffect(() => {
		if (isOpen && editingEvent) setValues(toEventFormValues(editingEvent, PLATFORM_OPTIONS, eventTypes, INITIAL_VALUES, groups));
	}, [editingEvent, eventTypes, groups, isOpen]);

	useEffect(() => {
		if (isOpen && !isEditMode && !values.eventType && eventTypes.length) patch('eventType', eventTypes[0]);
	}, [eventTypes, isEditMode, isOpen, patch, values.eventType]);

	const duration = useMemo(() => formatDuration(values.startTime, values.endTime), [values.startTime, values.endTime]);
	const today = useMemo(() => dayjs().startOf('day').toDate(), []);
	const endOfDay = useMemo(() => dayjs().endOf('day').toDate(), []);
	const startMinTime = useMemo(() => (isSameDayAsToday(values.date) ? getEarliestStartTimeForToday() : undefined), [values.date]);
	const isGroupMode = values.participantMode === ParticipantMode.GROUP;
	const isGroupLiveLesson = values.eventType?.code === EVENT_TYPE_CODE.GROUP_LIVE_LESSON;

	const validationErrors = useMemo<CreateEventFormErrors>(
		() => validateCreateEventValues(values, { isExpertRequired: !isEditMode && isGroupLiveLesson, requiresParticipants: !isEditMode }),
		[isEditMode, isGroupLiveLesson, values]
	);
	const getError = useCallback(
		(field: CreateEventFormField): string | undefined => (isSubmitAttempted || touchedFields.has(field) ? validationErrors[field] : undefined),
		[isSubmitAttempted, touchedFields, validationErrors]
	);

	const expertOptions = useMemo(() => (values.branch ? experts.filter(expert => expert.branches.some(branch => branch.id === values.branch?.id)) : experts), [experts, values.branch]);
	const expertSelectOptions = useMemo<SelectItem[]>(() => expertOptions.map(expert => ({ id: expert.id, name: expert.fullName })), [expertOptions]);
	const branchSelectOptions = useMemo<SelectItem[]>(() => branches.map(branch => ({ id: branch.id, name: branch.name })), [branches]);
	const selectedBranchOption = useMemo(() => branchSelectOptions.find(option => option.id === values.branch?.id) ?? null, [branchSelectOptions, values.branch]);
	const selectedExpertOption = useMemo(() => expertSelectOptions.find(option => option.id === values.expert?.id) ?? null, [expertSelectOptions, values.expert]);
	const selectedPlatform = useMemo(() => PLATFORM_OPTIONS.find(option => option.id === values.platform?.id) ?? null, [values.platform]);

	const [isStudentPickerOpen, setIsStudentPickerOpen] = useState(false);
	const [isMixedGroupsDialogOpen, setIsMixedGroupsDialogOpen] = useState(false);
	const [draftStudentIds, setDraftStudentIds] = useState<number[]>([]);
	const selectedStudents = useMemo(() => students.filter(student => values.studentIds.includes(student.id)), [students, values.studentIds]);

	const resetForm = useCallback(() => {
		setValues(INITIAL_VALUES);
		setTouchedFields(new Set());
		setIsSubmitAttempted(false);
		setIsStudentPickerOpen(false);
		setIsMixedGroupsDialogOpen(false);
		setDraftStudentIds([]);
	}, []);

	useEffect(() => {
		if (!isOpen) resetForm();
	}, [isOpen, resetForm]);

	const handleClose = useCallback(() => {
		resetForm();
		onClose();
	}, [onClose, resetForm]);

	const openStudentPicker = useCallback(() => {
		setDraftStudentIds(values.studentIds);
		setIsStudentPickerOpen(true);
	}, [values.studentIds]);

	const handleToggleDraftStudent = useCallback((studentId: number) => {
		setDraftStudentIds(prev => (prev.includes(studentId) ? prev.filter(id => id !== studentId) : [...prev, studentId]));
	}, []);

	const commitDraftStudents = useCallback(() => {
		patch('studentIds', draftStudentIds);
		setIsStudentPickerOpen(false);
	}, [draftStudentIds, patch]);

	const handleConfirmStudents = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			event.preventDefault();
			const draftStudents = students.filter(student => draftStudentIds.includes(student.id));
			if (hasStudentsFromMultipleGroups(draftStudents)) {
				setIsMixedGroupsDialogOpen(true);
				return;
			}
			commitDraftStudents();
		},
		[commitDraftStudents, draftStudentIds, students]
	);

	const handleCancelStudents = useCallback(() => {
		setIsStudentPickerOpen(false);
		if (values.studentIds.length === 0) patch('participantMode', ParticipantMode.GROUP);
	}, [patch, values.studentIds.length]);

	const handleSubmit = useCallback(
		(event: React.FormEvent<HTMLFormElement>) => {
			event.preventDefault();
			setIsSubmitAttempted(true);
			if (Object.keys(validationErrors).length > 0) {
				NotificationManager.showMessage(NOTIFICATION_TYPES.ERROR, EVENT_ERROR_MESSAGE.FORM_INVALID);
				return;
			}
			onSubmit(values);
		},
		[onSubmit, validationErrors, values]
	);

	const handleCancelMixedGroups = useCallback(() => setIsMixedGroupsDialogOpen(false), []);

	const handleConfirmMixedGroups = useCallback(() => {
		setIsMixedGroupsDialogOpen(false);
		commitDraftStudents();
	}, [commitDraftStudents]);

	const handleTypeSelect = useCallback(
		(eventType: ExpertEventType) => {
			patch('eventType', eventType);
			if (eventType.code !== EVENT_TYPE_CODE.GROUP_LIVE_LESSON) {
				patch('branch', null);
				patch('expert', null);
			}
		},
		[patch]
	);

	const handleBranchSelect = useCallback(
		(option: SelectItem) => {
			const branch = branches.find(item => item.id === option.id) ?? null;
			patchAndTouch('branch', branch);
			if (values.expert && branch && !values.expert.branches.some(item => item.id === branch.id)) patch('expert', null);
		},
		[branches, patch, patchAndTouch, values.expert]
	);

	const handleExpertSelect = useCallback((option: SelectItem) => patchAndTouch('expert', experts.find(item => item.id === option.id) ?? null), [experts, patchAndTouch]);

	const handleToggleGroup = useCallback(
		(option: SelectItem) => {
			const isSelected = values.groups.some(group => group.id === option.id);
			patchAndTouch('groups', isSelected ? values.groups.filter(group => group.id !== option.id) : [...values.groups, option]);
		},
		[patchAndTouch, values.groups]
	);

	const handleModeSelect = useCallback(
		(mode: ParticipantModeValue) => {
			patch('participantMode', mode);
			if (mode === ParticipantMode.STUDENT && values.studentIds.length === 0) {
				openStudentPicker();
			} else {
				setIsStudentPickerOpen(false);
			}
		},
		[openStudentPicker, patch, values.studentIds.length]
	);

	return (
		<>
			<Modal
				isOpen={isOpen}
				onClose={handleClose}
				hasHeader
				title={isEditMode ? EDIT_EVENT_TITLE : CREATE_EVENT_TITLE}
				showCloseButton
				size="xl"
				variant="centered"
				closeOnClickOutside={false}
				className="cem-modal"
				contentClassName="cem-modal__content"
				headerClassName="cem-modal__header"
				bodyClassName="cem-modal__body">
				<form
					className="cem"
					onSubmit={handleSubmit}
					noValidate>
					<div className="cem__scroll">
						{!isStudentPickerOpen && (
							<>
								<StepHeading
									step={1}
									title="Etkinlik Bilgileri"
								/>
								<FormField
									label="Etkinlik Türü"
									error={getError('eventType')}>
									{isLoadingOptions && !eventTypes.length ? (
										<p className="cem-hint">Etkinlik türleri yükleniyor...</p>
									) : (
										<div
											className="cem-type-grid"
											role="radiogroup"
											aria-label="Etkinlik Türü">
											{eventTypes.map((eventType, index) => (
												<EventTypeCard
													key={eventType.id}
													eventType={eventType}
													index={index}
													isSelected={values.eventType?.id === eventType.id}
													disabled={isEditMode}
													onSelect={handleTypeSelect}
												/>
											))}
										</div>
									)}
								</FormField>

								<div className="cem-row cem-row--2">
									<FormField
										label="Etkinlik Adı"
										htmlFor="cem-name"
										error={getError('name')}>
										<input
											id="cem-name"
											type="text"
											className="cem-input"
											value={values.name}
											maxLength={EVENT_NAME_MAX_LENGTH}
											placeholder={EVENT_NAME_PLACEHOLDER}
											onChange={event => patch('name', event.target.value)}
											onBlur={() => markTouched('name')}
										/>
									</FormField>
									<FormField
										label="Platform"
										error={getError('platform')}>
										<Dropdown<EventPlatformOption>
											className="cem-select"
											options={PLATFORM_OPTIONS}
											selectedOption={selectedPlatform}
											onOptionSelect={option => patchAndTouch('platform', option)}
											placeholder={SELECT_PLACEHOLDER}
											fullWidth
										/>
									</FormField>
								</div>

								<div className="cem-row cem-row--1">
									<FormField
										label="Kısa Açıklama"
										htmlFor="cem-short-description"
										error={getError('shortDescription')}>
										<input
											id="cem-short-description"
											type="text"
											className="cem-input"
											value={values.shortDescription}
											maxLength={EVENT_SHORT_DESCRIPTION_MAX_LENGTH}
											placeholder={EVENT_SHORT_DESCRIPTION_PLACEHOLDER}
											onChange={event => patch('shortDescription', event.target.value)}
											onBlur={() => markTouched('shortDescription')}
										/>
									</FormField>
								</div>

								{isGroupLiveLesson && !isEditMode && (
									<div className="cem-row cem-row--2">
										<FormField
											label="Branş"
											error={getError('branch')}>
											<Dropdown<SelectItem>
												className="cem-select"
												options={branchSelectOptions}
												selectedOption={selectedBranchOption}
												onOptionSelect={handleBranchSelect}
												placeholder={SELECT_PLACEHOLDER}
												fullWidth
											/>
										</FormField>
										<FormField
											label="Uzman"
											error={getError('expert')}>
											<Dropdown<SelectItem>
												className="cem-select"
												options={expertSelectOptions}
												selectedOption={selectedExpertOption}
												onOptionSelect={handleExpertSelect}
												placeholder={SELECT_PLACEHOLDER}
												disabled={!values.branch}
												fullWidth
											/>
										</FormField>
									</div>
								)}

								<StepHeading
									step={2}
									title="Zaman"
								/>
								<div className="cem-row cem-row--3">
									<FormField
										label="Tarih"
										error={getError('date')}>
										<DateTimeInput
											type="date"
											value={values.date}
											minDate={today}
											onChange={value => patchAndTouch('date', value)}
										/>
									</FormField>
									<FormField
										label="Başlangıç Saati"
										error={getError('startTime')}>
										<DateTimeInput
											type="time"
											value={values.startTime}
											minTime={startMinTime}
											maxTime={startMinTime ? endOfDay : undefined}
											onChange={value => patchAndTouch('startTime', value)}
										/>
									</FormField>
									<FormField
										label="Bitiş Saati"
										error={getError('endTime')}>
										<DateTimeInput
											type="time"
											value={values.endTime}
											minTime={values.startTime ?? undefined}
											maxTime={values.startTime ? endOfDay : undefined}
											onChange={value => patchAndTouch('endTime', value)}
										/>
									</FormField>
								</div>
								<div className="cem-meta">
									<span className="cem-meta__duration">
										Süre: <strong>{duration}</strong>
									</span>
								</div>
							</>
						)}

						{!isEditMode && (
							<>
								<StepHeading
									step={3}
									title="Katılımcılar"
								/>
								<div
									className="cem-segment"
									role="tablist">
									{PARTICIPANT_MODES.map(mode => (
										<button
											key={mode}
											type="button"
											role="tab"
											aria-selected={values.participantMode === mode}
											className={clsx('cem-segment__item', { 'cem-segment__item--active': values.participantMode === mode })}
											onClick={() => handleModeSelect(mode)}>
											{ParticipantModeLabel[mode]}
										</button>
									))}
								</div>

								{isGroupMode ? (
									<>
										<FormField
											label="Gruplar"
											error={getError('groups')}>
											<div className="cem-inline">
												<GroupMultiSelect
													className="cem-select"
													options={groups}
													selected={values.groups}
													onToggle={handleToggleGroup}
													placeholder={SELECT_PLACEHOLDER}
													emptyText={EVENT_GROUPS_EMPTY_TEXT}
												/>
												<button
													type="button"
													className="cem-link"
													onClick={() => onCreateGroup?.(selectedStudents)}>
													+ Yeni Grup Oluştur
												</button>
											</div>
										</FormField>
										<div className="cem-info">
											<i className="icons-info-circle" />
											<span>Seçilen gruplardaki tüm öğrenciler etkinliğe otomatik olarak eklenecektir.</span>
										</div>
									</>
								) : isStudentPickerOpen ? (
									<StudentPickerList
										students={students}
										selectedIds={draftStudentIds}
										onToggle={handleToggleDraftStudent}
									/>
								) : (
									<div className="cem-field">
										<div className="cem-field__header">
											<span className="cem-field__label">Seçilen Öğrenciler ({selectedStudents.length})</span>
											<button
												type="button"
												className="cem-link"
												onClick={openStudentPicker}>
												+ Öğrenci Ekle
											</button>
										</div>
										<StudentPickerList
											students={selectedStudents}
											selectedIds={values.studentIds}
											onToggle={studentId =>
												patch(
													'studentIds',
													values.studentIds.filter(id => id !== studentId)
												)
											}
											emptyText="Henüz öğrenci seçilmedi."
										/>
										{getError('studentIds') && (
											<span
												className="cem-field__error"
												role="alert">
												{getError('studentIds')}
											</span>
										)}
									</div>
								)}
							</>
						)}

						{isEditMode && Boolean(values.groups.length) && (
							<>
								<StepHeading
									step={3}
									title="Katılımcılar"
								/>
								<FormField label="Gruplar">
									<GroupMultiSelect
										className="cem-select"
										options={groups}
										selected={values.groups}
										onToggle={handleToggleGroup}
										placeholder={SELECT_PLACEHOLDER}
										emptyText={EVENT_GROUPS_EMPTY_TEXT}
										disabled
									/>
								</FormField>
								<div className="cem-info">
									<i className="icons-info-circle" />
									<span>{EVENT_GROUPS_EDIT_LOCKED_TEXT}</span>
								</div>
							</>
						)}

						{!isStudentPickerOpen && (
							<div className="cem-row cem-row--1">
								<FormField
									label="Katılım Linki"
									htmlFor="cem-link"
									error={getError('participationLink')}>
									<input
										id="cem-link"
										type="url"
										inputMode="url"
										className="cem-input"
										value={values.participationLink}
										placeholder={EVENT_LINK_PLACEHOLDER}
										onChange={event => patch('participationLink', event.target.value)}
										onBlur={() => markTouched('participationLink')}
									/>
								</FormField>
							</div>
						)}
					</div>

					<div className="cem__footer">
						<button
							type="button"
							className="cem-button cem-button--secondary"
							onClick={isStudentPickerOpen ? handleCancelStudents : handleClose}
							disabled={isSubmitting}>
							İptal
						</button>
						{isStudentPickerOpen ? (
							<button
								key="confirm-students"
								type="button"
								className="cem-button cem-button--primary"
								onClick={handleConfirmStudents}
								disabled={draftStudentIds.length === 0}>
								{STUDENT_PICKER_SUBMIT_LABEL}
							</button>
						) : (
							<button
								key="submit-event"
								type="submit"
								className="cem-button cem-button--primary"
								disabled={isSubmitting}>
								{isEditMode ? EDIT_EVENT_SUBMIT_LABEL : CREATE_EVENT_SUBMIT_LABEL}
							</button>
						)}
					</div>
				</form>
			</Modal>
			<DialogModal
				isOpen={isMixedGroupsDialogOpen}
				onClose={handleCancelMixedGroups}
				variant="warning"
				title={MIXED_GROUPS_DIALOG.TITLE}
				primaryButtonText={MIXED_GROUPS_DIALOG.CONFIRM_LABEL}
				secondaryButtonText={MIXED_GROUPS_DIALOG.CANCEL_LABEL}
				onPrimaryClick={handleConfirmMixedGroups}
				onSecondaryClick={handleCancelMixedGroups}
				showCloseIcon={false}>
				<p>{MIXED_GROUPS_DIALOG.DESCRIPTION}</p>
			</DialogModal>
		</>
	);
};

export default CreateEventModal;
